import { Clock } from 'lucide-react';

import { useMessageRateLimitCapacityQuery } from '@/modules/user-external-accounts/queries/useMessageRateLimitCapacityQuery';
import { pausedLimit } from '@/modules/user-external-accounts/utils/sendingLimits';

interface SendingLimitsProps {
  /** The LinkedIn account Finlay sends from. */
  accountId: string;
}

/**
 * Says so when Finlay's own sending cap for the account is used up, and
 * that the held-back messages go out by themselves. Nothing otherwise, and
 * nothing while the capacity is unknown: it is extra context, not a state
 * the page depends on.
 */
export function SendingPausedNotice({ accountId }: SendingLimitsProps) {
  const capacityQuery = useMessageRateLimitCapacityQuery(accountId);
  const limit = capacityQuery.data && pausedLimit(capacityQuery.data);
  if (!limit) return null;

  return (
    <p className="bg-muted text-muted-foreground flex items-start gap-3 rounded-lg px-4 py-3 text-[13px] leading-[18px]">
      <Clock aria-hidden className="mt-px size-4 shrink-0" />
      <span>
        <span className="text-foreground font-medium">
          Finlay has sent {limit.period === 'today' ? 'today’s' : 'this week’s'}{' '}
          {limit.max.toLocaleString()} LinkedIn {limit.kind}.
        </span>{' '}
        The rest stay queued and go out on their own as the limit frees up.
      </span>
    </p>
  );
}

/** What is left of the account's sending caps, in one line. */
export function SendingLimitsSummary({ accountId }: SendingLimitsProps) {
  const capacityQuery = useMessageRateLimitCapacityQuery(accountId);
  const capacity = capacityQuery.data;
  if (!capacity) return null;

  const left = (remaining: number, max: number) =>
    `${Math.max(0, remaining).toLocaleString()} of ${max.toLocaleString()}`;
  return (
    <p className="text-[13px] text-neutral-500 tabular-nums">
      Sending limits:{' '}
      {left(
        capacity.remainingInvitationsThisWeek,
        capacity.maxInvitationsPerWeek,
      )}{' '}
      invitations left this week (
      {left(capacity.remainingInvitationsToday, capacity.maxInvitationsPerDay)}{' '}
      today) and{' '}
      {left(capacity.remainingMessagesThisWeek, capacity.maxMessagesPerWeek)}{' '}
      messages (
      {left(capacity.remainingMessagesToday, capacity.maxMessagesPerDay)}{' '}
      today).
    </p>
  );
}
